import { useEffect, useMemo, useRef, useState } from "react";

export default function PointField({
  id,
  name,
  className,
  label,
  value,
  scale,
  precision,
  latPlaceholder,
  lonPlaceholder,
  onChange,
  isInvalid,
  error,
  help,
  labelStyle,
  disabled,
  ...props
}) {
  const inputRef = useRef();
  const handleChange = useMemo(
    () => (event) => {
      if (event.target === inputRef.current) {
        if (onChange instanceof Function) {
          onChange(event);
        }
      } else {
        event.stopPropagation();
      }
    },
    [onChange],
  );
  const [point, setPoint] = useState(
    (!!value && {
      type: value.type,
      coordinates: [value?.coordinates?.[0], value?.coordinates?.[1]],
    }) || { type: "Point", coordinates: [0, 0] },
  );
  const computedClassName = [
    "form-group",
    "point-field",
    isInvalid ? "has-error" : "",
    ...className.split(" "),
  ]
    .join(" ")
    .trim();

  const handleCoordinateChange = (index) => (event) => {
    const value = point;
    value.coordinates[index] = event.currentTarget.value;
    inputRef.current.value = value;
    inputRef.current.name = name;
    inputRef.current.dispatchEvent(
      new Event("change", {
        bubbles: true,
        cancelable: true,
        composed: false,
      }),
    );
    setPoint(value);
  };
  const handleCoordinateBlur = (index) => (event) => {
    const value = point;
    value.coordinates[index] = event.currentTarget.value;
    inputRef.current.value = value;
    inputRef.current.name = name;
    inputRef.current.dispatchEvent(
      new Event("change", {
        bubbles: true,
        cancelable: true,
        composed: false,
      }),
    );
  };
  useEffect(() => {
    const input = inputRef.current;
    const onChange = handleChange;
    if (!!input) {
      input.addEventListener("change", onChange);
      return () => {
        input.removeEventListener("change", onChange);
      };
    }
  }, [handleChange]);
  useEffect(() => {
    setPoint(
      (!!value && {
        type: value.type,
        coordinates: [value?.coordinates?.[0], value?.coordinates?.[1]],
      }) || { type: "Point", coordinates: [0, 0] },
    );
  }, [precision, scale, value]);
  return (
    <div className={computedClassName} {...props}>
      <label for={id} style={labelStyle} className="control-label">
        {label}
      </label>
      <span className="input-group" id={id} ref={inputRef}>
        <div className="input-group-addon">&#40;</div>
        <input
          id={!!id && `${id}_coordinates_0`}
          type="number"
          step={1 / Math.pow(10, scale)}
          maxLength={precision}
          placeholder={latPlaceholder}
          value={point.coordinates[0]}
          onChange={handleCoordinateChange(0)}
          onBlur={handleCoordinateBlur(0)}
          className="form-control"
          aria-invalid={isInvalid}
          disabled={disabled}
        />
        <div className="input-group-addon">&#44;</div>
        <input
          id={!!id && `${id}_coordinates_1`}
          type="number"
          step={1 / Math.pow(10, scale)}
          placeholder={lonPlaceholder}
          maxLength={precision}
          className="form-control"
          value={point.coordinates[1]}
          onChange={handleCoordinateChange(1)}
          onBlur={handleCoordinateBlur(1)}
          aria-invalid={isInvalid}
          disabled={disabled}
        />
        <div className="input-group-addon">&#41;</div>
      </span>
      {isInvalid && !!error && <span className="help-block">{error}</span>}
    </div>
  );
}

PointField.defaultProps = {
  precission: 12,
  scale: 9,
  className: "",
  latPlaceholder: "lat",
  lonPlaceholder: "lon",
};
